import React, { useCallback, useMemo } from 'react'
import { Table, Button, Badge, Tooltip, Whisper, Message } from 'rsuite'
import { format } from 'date-fns'
import { ru } from 'date-fns/locale'

import LegacyEditIcon from '@rsuite/icons/legacy/Edit'
import LegacyTrashIcon from '@rsuite/icons/legacy/Trash'
import LegacyPlayIcon from '@rsuite/icons/legacy/Play'
import LegacyPauseIcon from '@rsuite/icons/legacy/Pause'
import LegacyDownloadIcon from '@rsuite/icons/legacy/Download'

import type { ReportSchedule } from '@@partner:types:app'

import './ReportSchedulesTable.scss'
import { GQL_ID } from '@@partner:types:graphql'

const { Column, HeaderCell, Cell } = Table

interface ReportSchedulesTableProps {
  data: ReportSchedule[]
  loading?: boolean
  onEdit: (schedule: ReportSchedule) => void
  onDelete: (scheduleId: GQL_ID) => void
  onToggle: (scheduleId: GQL_ID, isActive: boolean) => void
  onGenerate: (schedule: ReportSchedule) => void
  sortColumn?: string
  sortType?: 'asc' | 'desc'
  onSortColumn?: (dataKey: string, sortType?: 'asc' | 'desc') => void
}

const ReportSchedulesTable: React.FC<ReportSchedulesTableProps> = ({
  data,
  loading = false,
  onEdit,
  onDelete,
  onToggle,
  onGenerate,
  sortColumn,
  sortType,
  onSortColumn
}) => {
  const handleEdit = useCallback((rowData: ReportSchedule) => {
    onEdit(rowData)
  }, [ onEdit ])

  const handleDelete = useCallback((rowData: ReportSchedule) => {
    onDelete(rowData.id)
  }, [ onDelete ])

  const handleToggle = useCallback((rowData: ReportSchedule) => {
    onToggle(rowData.id, !rowData.is_enabled)
  }, [ onToggle ])

  const handleGenerate = useCallback((rowData: ReportSchedule) => {
    onGenerate(rowData)
  }, [ onGenerate ])

  const formatDate = useCallback((date: string | null) => {
    if (!date) return 'Никогда'
    return format(new Date(date), 'dd.MM.yyyy HH:mm', { locale: ru })
  }, [])

  const getStatusBadge = useCallback((isActive: boolean) => {
    return (
      <Badge color={isActive ? 'green' : 'red'}>
        {isActive ? 'Активен' : 'Неактивен'}
      </Badge>
    )
  }, [])

  const renderEmailRecipients = useCallback((recipients: string | string[]) => {
    const emailList = Array.isArray(recipients) ? recipients : JSON.parse(recipients || '[]')
    if (emailList.length === 0) return 'Не указаны'
    if (emailList.length === 1) return emailList[0]
    
    return (
      <Whisper
        trigger="hover"
        placement="top"
        speaker={
          <Tooltip>
            {emailList.join(', ')}
          </Tooltip>
        }
      >
        <span>{`${emailList[0]} и еще ${emailList.length - 1}`}</span>
      </Whisper>
    )
  }, [])

  const ActionCell = ({ rowData, ...props }: any) => (
    <Cell {...props} className="link-group">
      <Whisper speaker={<Tooltip>Редактировать</Tooltip>}>
        <Button
          appearance="link"
          size="sm"
          onClick={() => handleEdit(rowData)}
        >
          <LegacyEditIcon />
        </Button>
      </Whisper>
      
      <Whisper speaker={<Tooltip>{rowData.is_enabled ? 'Приостановить' : 'Активировать'}</Tooltip>}>
        <Button
          appearance="link"
          size="sm"
          onClick={() => handleToggle(rowData)}
        >
          {rowData.is_enabled ? <LegacyPauseIcon /> : <LegacyPlayIcon />}
        </Button>
      </Whisper>

      <Whisper speaker={<Tooltip>Сгенерировать сейчас</Tooltip>}>
        <Button
          appearance="link"
          size="sm"
          onClick={() => handleGenerate(rowData)}
        >
          <LegacyDownloadIcon />
        </Button>
      </Whisper>

      <Whisper speaker={<Tooltip>Удалить</Tooltip>}>
        <Button
          appearance="link"
          size="sm"
          onClick={() => handleDelete(rowData)}
        >
          <LegacyTrashIcon />
        </Button>
      </Whisper>
    </Cell>
  )

  return (
    <div className="ReportSchedulesTable">
      <Table
        data={data}
        loading={loading}
        sortColumn={sortColumn}
        sortType={sortType}
        onSortColumn={onSortColumn}
        height={600}
      >
        <Column width={80} align="center" fixed>
          <HeaderCell>ID</HeaderCell>
          <Cell dataKey="id" />
        </Column>

        <Column width={200} sortable>
          <HeaderCell>Название</HeaderCell>
          <Cell dataKey="name" />
        </Column>

        <Column width={150} sortable>
          <HeaderCell>Тип отчета</HeaderCell>
          <Cell>
            {(rowData: ReportSchedule) => rowData.reportType?.name || 'Неизвестен'}
          </Cell>
        </Column>

        <Column width={150}>
          <HeaderCell>Магазины</HeaderCell>
          <Cell>
            {(rowData: ReportSchedule) => {
              if (!rowData.shops || rowData.shops.length === 0) {
                return 'Все магазины'
              }
              if (rowData.shops.length === 1) {
                return rowData.shops[0].name
              }
              return `${rowData.shops.length} магазин(ов)`
            }}
          </Cell>
        </Column>

        <Column width={120} sortable>
          <HeaderCell>Расписание</HeaderCell>
          <Cell dataKey="frequency" />
        </Column>

        <Column width={200}>
          <HeaderCell>Получатели</HeaderCell>
          <Cell>
            {(rowData: ReportSchedule) => renderEmailRecipients(rowData.recipients)}
          </Cell>
        </Column>

        <Column width={100} align="center">
          <HeaderCell>Статус</HeaderCell>
          <Cell>
            {(rowData: ReportSchedule) => getStatusBadge(rowData.is_enabled)}
          </Cell>
        </Column>

        <Column width={150} sortable>
          <HeaderCell>Последний запуск</HeaderCell>
          <Cell>
            {(rowData: ReportSchedule) => formatDate(rowData.last_run_at)}
          </Cell>
        </Column>

        <Column width={150} sortable>
          <HeaderCell>Следующий запуск</HeaderCell>
          <Cell>
            {(rowData: ReportSchedule) => formatDate(rowData.next_run_at)}
          </Cell>
        </Column>

        <Column width={180} fixed="right">
          <HeaderCell>Действия</HeaderCell>
          <ActionCell />
        </Column>
      </Table>
    </div>
  )
}

export default ReportSchedulesTable
